<script lang="ts">
  import { showAlert } from '../lib/ui-alert';
  import FieldTypeIcon from './FieldTypeIcon.svelte';
  import Icon from './Icon.svelte';
  import { TABLE_ICON_CHOICES, tableIconName } from '../lib/icons';
  import { onMount } from 'svelte';
  import FieldsBoard from './FieldsBoard.svelte';
  import SnapshotsPanel from './SnapshotsPanel.svelte';
  import { FIELD_TYPES, fieldType, type FieldTypeKey } from '../lib/data-tables-catalog';
  import {
    fetchDataTables,
    createDataTable,
    updateDataTable,
    deleteDataTable,
    createDataField,
    updateDataField,
    deleteDataField,
    reorderDataFields,
    type ApiTable,
    type ApiField,
  } from '../lib/data-tables-api';
  import { invalidateCustomTables } from '../lib/custom-tables-store';

  /** Якщо передано — після завантаження одразу відкриває detail цієї таблиці. */
  let { focusSlug }: { focusSlug?: string } = $props();

  let tables = $state<ApiTable[]>([]);
  let loading = $state(true);
  let err = $state<string | null>(null);

  type View =
    | { kind: 'list' }
    | { kind: 'detail'; slug: string; tab: 'folder' | 'fields' | 'snapshots' }
    | { kind: 'pick-type'; slug: string; carryForm?: boolean }
    | { kind: 'field-form'; slug: string; fieldId?: string; type: FieldTypeKey };

  let view = $state<View>({ kind: 'list' });
  let busy = $state(false);

  // ── Folder-settings edit (вкладка ПАПКА) ───────────────────────────────
  let fName = $state('');
  let fColor = $state('');
  let fIcon = $state('');
  let fDesc = $state('');
  let folderBusy = $state(false);
  let folderSaved = $state(false);
  let folderInitSlug = $state<string | null>(null);

  /** Ініціалізує форму поточними значеннями таблиці. */
  function initFolderForm(tbl: ApiTable) {
    fName = tbl.name;
    fColor = tbl.color;
    fIcon = tbl.icon;
    fDesc = tbl.description ?? '';
    folderInitSlug = tbl.slug;
    folderSaved = false;
  }

  // Синхронізуємо форму при відкритті вкладки / зміні таблиці.
  $effect(() => {
    const v = view;
    if (v.kind === 'detail' && v.tab === 'folder') {
      const tbl = tables.find((x) => x.slug === v.slug);
      if (tbl && folderInitSlug !== tbl.slug) initFolderForm(tbl);
    }
  });

  const folderDirty = $derived.by(() => {
    const v = view;
    if (v.kind !== 'detail') return false;
    const tbl = tables.find((x) => x.slug === v.slug);
    if (!tbl) return false;
    return (
      fName.trim() !== tbl.name ||
      fIcon !== tbl.icon ||
      fColor !== tbl.color ||
      fDesc.trim() !== (tbl.description ?? '')
    );
  });

  async function saveFolder() {
    const v = view;
    if (v.kind !== 'detail' || !fName.trim()) return;
    const slug = v.slug;
    folderBusy = true;
    try {
      await updateDataTable(slug, {
        name: fName.trim(),
        icon: fIcon,
        color: fColor,
        description: fDesc.trim() || null,
      });
      await load();
      invalidateCustomTables();
      const tbl = tables.find((x) => x.slug === slug);
      if (tbl) initFolderForm(tbl);
      folderSaved = true;
      setTimeout(() => (folderSaved = false), 2500);
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      folderBusy = false;
    }
  }

  // Form state for field editor (kept top-level so it survives navigation
  // through "Змінити тип" -> pick-type -> field-form).
  let formLabel = $state('');
  let formRequired = $state(false);
  /** Назва групи у формі поля. Пусто = "без групи". */
  let formGroupName = $state('');
  /** Опції dropdown — один рядок = одна опція. Зберігається текстом у formі,
   *  спліттиться по \n при save. Пусто = не змінювати. */
  let formOptionsText = $state('');
  /** Чи показувати поле колонкою у списку (Persons/Payments/CustomTable). */
  let formShowInList = $state(true);
  /** Target table slug для linked_record/linked_summary полів. */
  let formTargetSlug = $state('');
  /** Cardinality для linked_record: 'one' або 'many'. */
  let formCardinality = $state<'one' | 'many'>('one');
  /** Expression для type='formula'. */
  let formFormulaExpression = $state('');
  /** Stat config поля (type='stat'). V1 MVP: target_slug фіксований 'payments'. */
  let formStatAggregator = $state<'count' | 'sum' | 'avg' | 'min' | 'max'>('count');
  let formStatTargetSlug = $state('payments');
  let formStatLinkField = $state('personId');
  let formStatTargetField = $state('amountCents');
  /** Phase 4 per-field permissions: мінімальні ролі. '' = без обмеження. */
  let formMinViewRole = $state<'' | 'READER' | 'ANALYST' | 'ADMIN'>('');
  let formMinEditRole = $state<'' | 'READER' | 'ANALYST' | 'ADMIN'>('');

  // Create table modal state.
  let createTableOpen = $state(false);
  let newTableName = $state('');
  let newTableSlug = $state('');
  let newTableIcon = $state('folder');
  let newTableColor = $state('#9b7fdb');

  async function load() {
    loading = true;
    err = null;
    try {
      const r = await fetchDataTables();
      tables = r.items;
      // Deep-link: якщо передано focusSlug — одразу відкриваємо detail
      // (тільки коли ми ще у list-view, щоб не зривати active form).
      if (focusSlug && view.kind === 'list' && tables.some((t) => t.slug === focusSlug)) {
        view = { kind: 'detail', slug: focusSlug, tab: 'fields' };
      }
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }
  onMount(load);

  function openTable(slug: string) {
    view = { kind: 'detail', slug, tab: 'fields' };
  }
  function backToList() {
    view = { kind: 'list' };
  }
  function setTab(tab: 'folder' | 'fields' | 'snapshots') {
    if (view.kind === 'detail') view = { ...view, tab };
  }

  function slugify(name: string): string {
    return (
      name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9_-]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .replace(/^[^a-z]/, 'x-') || 'table'
    );
  }

  async function createTable(e: SubmitEvent) {
    e.preventDefault();
    const name = newTableName.trim();
    if (!name) return;
    const slug = newTableSlug.trim() || slugify(name);
    busy = true;
    try {
      await createDataTable({ slug, name, icon: newTableIcon || 'folder', color: newTableColor });
      createTableOpen = false;
      newTableName = '';
      newTableSlug = '';
      await load();
      invalidateCustomTables();
      view = { kind: 'detail', slug, tab: 'fields' };
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
    }
  }

  async function onDeleteTable(slug: string) {
    const t = tables.find((x) => x.slug === slug);
    if (!t || t.builtIn) return;
    if (!confirm(`Видалити таблицю "${t.name}" разом з її кастомними полями?`)) return;
    busy = true;
    try {
      await deleteDataTable(slug);
      view = { kind: 'list' };
      await load();
      invalidateCustomTables();
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
    }
  }

  function selectedTable(): ApiTable | null {
    const v = view;
    if (v.kind === 'list') return null;
    return tables.find((t) => t.slug === v.slug) ?? null;
  }

  // =========================================================================
  //  Field editor flow
  // =========================================================================

  function openPickType(slug: string, presetGroupName?: string | null) {
    formLabel = '';
    formRequired = false;
    formGroupName = presetGroupName ?? '';
    formOptionsText = '';
    formShowInList = true;
    formTargetSlug = '';
    formFormulaExpression = '';
    formStatAggregator = 'count';
    formStatTargetSlug = 'payments';
    formStatLinkField = 'personId';
    formStatTargetField = 'amountCents';
    formMinViewRole = '';
    formMinEditRole = '';
    view = { kind: 'pick-type', slug };
  }

  function selectFieldType(type: FieldTypeKey) {
    if (view.kind !== 'pick-type') return;
    const ft = fieldType(type);
    if (!formLabel) formLabel = ft.label;
    view = { kind: 'field-form', slug: view.slug, type };
  }

  function openFieldEdit(slug: string, f: ApiField) {
    formLabel = f.label;
    formRequired = f.required;
    formGroupName = f.groupName ?? '';
    formOptionsText = (f.options ?? []).join('\n');
    formShowInList = f.show_in_list !== false;
    formTargetSlug = f.target_slug ?? '';
    formCardinality = (f.cardinality === 'many' ? 'many' : 'one');
    formFormulaExpression = f.formula_expression ?? '';
    formStatAggregator = (f.stat_config?.aggregator ?? 'count') as typeof formStatAggregator;
    formStatTargetSlug = f.stat_config?.target_slug ?? 'payments';
    formStatLinkField = f.stat_config?.link_field ?? 'personId';
    formStatTargetField = f.stat_config?.target_field ?? 'amountCents';
    formMinViewRole = (f.min_view_role ?? '') as '' | 'READER' | 'ANALYST' | 'ADMIN';
    formMinEditRole = (f.min_edit_role ?? '') as '' | 'READER' | 'ANALYST' | 'ADMIN';
    view = { kind: 'field-form', slug, fieldId: f.id, type: f.type };
  }

  function changeTypeFromForm() {
    if (view.kind !== 'field-form') return;
    // Дозволяємо тільки для create (нового поля). Для існуючих — type
    // фіксований, бо може зруйнувати дані.
    if (view.fieldId) return;
    view = { kind: 'pick-type', slug: view.slug, carryForm: true };
  }

  function backFromForm() {
    if (view.kind === 'field-form' || view.kind === 'pick-type') {
      view = { kind: 'detail', slug: view.slug, tab: 'fields' };
    }
  }

  /** Усі вже існуючі (не-builtIn) groupNames у цій таблиці — для datalist. */
  function existingGroupNames(slug: string): string[] {
    const t = tables.find((x) => x.slug === slug);
    if (!t) return [];
    const set = new Set<string>();
    for (const f of t.fields) {
      if (f.builtIn) continue;
      if (f.groupName) set.add(f.groupName);
    }
    return Array.from(set).sort();
  }

  /**
   * Згрупувати custom-поля таблиці за groupName.
   * Built-in поля рендеряться окремо (вгорі списку), вони не входять у групи.
   */
  function groupedCustomFields(t: ApiTable): { groupName: string | null; fields: ApiField[] }[] {
    const map = new Map<string | null, ApiField[]>();
    for (const f of t.fields) {
      if (f.builtIn) continue;
      const key = f.groupName ?? null;
      const arr = map.get(key) ?? [];
      arr.push(f);
      map.set(key, arr);
    }
    // Сортуємо групи: спочатку названі (за алфавітом), потім "без групи" внизу.
    const named = Array.from(map.entries())
      .filter(([k]) => k !== null)
      .sort(([a], [b]) => (a as string).localeCompare(b as string));
    const unnamed = map.has(null) ? [[null, map.get(null)!] as [string | null, ApiField[]]] : [];
    return [...named, ...unnamed].map(([groupName, fields]) => ({
      groupName,
      fields: fields.sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    }));
  }

  /** Перемістити поле вгору/вниз у межах своєї групи (bulk reorder). */
  async function moveField(slug: string, groupName: string | null, fieldId: string, delta: 1 | -1) {
    const t = tables.find((x) => x.slug === slug);
    if (!t) return;
    const inGroup = t.fields
      .filter((f) => !f.builtIn && (f.groupName ?? null) === groupName)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const idx = inGroup.findIndex((f) => f.id === fieldId);
    if (idx < 0) return;
    const nextIdx = idx + delta;
    if (nextIdx < 0 || nextIdx >= inGroup.length) return;
    [inGroup[idx], inGroup[nextIdx]] = [inGroup[nextIdx]!, inGroup[idx]!];
    busy = true;
    try {
      await reorderDataFields(
        slug,
        inGroup.map((f, i) => ({ id: f.id, position: i, groupName })),
      );
      await load();
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
    }
  }

  /** Перенести поле в іншу групу (відкриває prompt). */
  async function moveToGroup(slug: string, fieldId: string) {
    const currentGroups = existingGroupNames(slug);
    const hint = currentGroups.length
      ? `\nІснуючі: ${currentGroups.join(', ')}`
      : '';
    const name = prompt(`Введіть назву групи (пусто = без групи):${hint}`, '');
    if (name === null) return; // cancel
    const groupName = name.trim() || null;
    busy = true;
    try {
      await updateDataField(slug, fieldId, { groupName });
      await load();
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
    }
  }

  async function saveField() {
    if (view.kind !== 'field-form') return;
    const label = formLabel.trim();
    if (!label) {
      showAlert("Назва поля обов'язкова");
      return;
    }
    busy = true;
    try {
      const groupName = formGroupName.trim() || null;
      // Options для dropdown: парсимо textarea (один рядок = одна опція).
      const options =
        view.type === 'dropdown'
          ? formOptionsText
              .split('\n')
              .map((s) => s.trim())
              .filter(Boolean)
          : undefined;
      const isLinked = view.type === 'linked_record' || view.type === 'linked_summary';
      const targetSlug = isLinked ? formTargetSlug.trim() || null : undefined;
      const cardinality = isLinked && view.type === 'linked_record' ? formCardinality : undefined;
      const formulaExpression =
        view.type === 'formula' ? formFormulaExpression.trim() || null : undefined;
      const statConfig =
        view.type === 'stat'
          ? {
              aggregator: formStatAggregator,
              target_slug: formStatTargetSlug,
              link_field: formStatLinkField,
              ...(formStatAggregator !== 'count' ? { target_field: formStatTargetField } : {}),
            }
          : undefined;
      const minViewRole = formMinViewRole === '' ? null : formMinViewRole;
      const minEditRole = formMinEditRole === '' ? null : formMinEditRole;
      if (view.fieldId) {
        await updateDataField(view.slug, view.fieldId, {
          label,
          required: formRequired,
          groupName,
          showInList: formShowInList,
          ...(options !== undefined ? { options } : {}),
          ...(targetSlug !== undefined ? { targetSlug } : {}),
          ...(cardinality !== undefined ? { cardinality } : {}),
          ...(formulaExpression !== undefined ? { formulaExpression } : {}),
          ...(statConfig !== undefined ? { statConfig } : {}),
          minViewRole,
          minEditRole,
        });
      } else {
        await createDataField(view.slug, {
          label,
          type: view.type,
          required: formRequired,
          groupName,
          showInList: formShowInList,
          ...(options !== undefined ? { options } : {}),
          ...(targetSlug ? { targetSlug } : {}),
          ...(cardinality ? { cardinality } : {}),
          ...(formulaExpression ? { formulaExpression } : {}),
          ...(statConfig ? { statConfig } : {}),
          ...(minViewRole ? { minViewRole } : {}),
          ...(minEditRole ? { minEditRole } : {}),
        });
      }
      await load();
      view = { kind: 'detail', slug: view.slug, tab: 'fields' };
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
    }
  }

  async function onDeleteField(slug: string, fieldId: string) {
    if (!confirm('Видалити поле?')) return;
    busy = true;
    try {
      await deleteDataField(slug, fieldId);
      await load();
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
    }
  }

  function selectedField(): ApiField | null {
    const v = view;
    if (v.kind !== 'field-form' || !v.fieldId) return null;
    const t = selectedTable();
    return t?.fields.find((f) => f.id === v.fieldId) ?? null;
  }

  /** Опис типу поля під заголовком у формі. */
  function typeDescription(type: FieldTypeKey): string {
    switch (type) {
      case 'single_line':
        return 'Однорядковий текст — імена, заголовки, короткі значення.';
      case 'multi_line':
        return 'Багаторядковий текст — нотатки, описи, коментарі.';
      case 'dropdown':
        return 'Випадаюче меню — вибір однієї опції зі списку.';
      case 'date':
        return 'Дата події.';
      case 'datetime':
        return 'Дата і час події.';
      case 'email':
        return "Зберігайте адреси електронної пошти, автоматично пов'язуйте майбутні листи.";
      case 'phone':
        return 'Телефонні номери у міжнародному форматі.';
      case 'number':
        return 'Цілі або десяткові числа.';
      case 'auto_number':
        return 'Автоматичний інкрементний номер.';
      case 'percent':
        return 'Відсоткове значення (0–100).';
      case 'currency':
        return 'Грошова сума (для LTV, суми платежу, ціни).';
      case 'url':
        return 'Будь-яке посилання (URL).';
      case 'country':
        return 'Країна (ISO-код).';
      case 'tags':
        return 'Декілька тегів-пілюль (multi-value). Натисни Enter або кому щоб додати.';
      case 'checkbox':
        return 'Так / Ні.';
      case 'formula':
        return 'Обчислюване поле з інших полів запису.';
      case 'stat':
        return 'Агреговане число (сума, середнє тощо).';
      case 'linked_record':
        return "Посилання на один або кілька записів іншої таблиці.";
      case 'linked_summary':
        return "Підсумок (sum/count) з пов'язаних записів.";
    }
  }

  const PALETTE: { color: string; label: string }[] = [
    { color: '#9b7fdb', label: 'Фіолетовий' },
    { color: '#8088db', label: 'Індиго' },
    { color: '#6f9fe0', label: 'Синій' },
    { color: '#5cb6dc', label: 'Блакитний' },
    { color: '#4fb8a8', label: 'Бірюзовий' },
    { color: '#63bd8c', label: 'Зелений' },
    { color: '#a3c073', label: 'Лаймовий' },
    { color: '#e6bd6b', label: 'Бурштиновий' },
    { color: '#e89f6a', label: 'Помаранчевий' },
    { color: '#e2868f', label: 'Кораловий' },
    { color: '#da82b4', label: 'Рожевий' },
    { color: '#b58e72', label: 'Коричневий' },
    { color: '#94a0b3', label: 'Сірий' },
  ];

  const ICONS = TABLE_ICON_CHOICES;

  function groupedFieldTypes() {
    return {
      common: FIELD_TYPES.filter((t) => t.category === 'common'),
      advanced: FIELD_TYPES.filter((t) => t.category === 'advanced'),
    };
  }
</script>

{#if loading}
  <div class="py-20 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
{:else if err}
  <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
{:else if view.kind === 'list'}
  <!-- ============== LIST VIEW ============== -->
  <header class="mb-5">
    <h1 class="text-xl font-semibold leading-tight">Управління таблицями</h1>
    <p class="mt-1 text-[12px] text-[var(--color-text-muted)]">
      Сутності системи та конфігурація їхніх полів
    </p>
  </header>

  <div class="overflow-hidden rounded-xl border border-[var(--color-border-3)] bg-white">
    {#each tables as t, i (t.slug)}
      <button
        type="button"
        onclick={() => openTable(t.slug)}
        class="flex w-full items-center gap-4 px-5 py-4 text-left transition hover:bg-[var(--color-bg-3)]"
        class:border-t={i > 0}
        class:border-[var(--color-border-3)]={i > 0}
      >
        <div
          class="grid h-11 w-11 shrink-0 place-items-center rounded-full text-white"
          style="background: {t.color}"
        >
          <Icon name={tableIconName(t.icon)} size={20} />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class="text-[15px] font-semibold leading-tight">{t.name}</span>
            {#if t.builtIn}
              <span class="rounded-full bg-[var(--color-pu-light)] px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-[var(--color-pu-3)]">
                Built-in
              </span>
            {/if}
          </div>
          <div class="mt-1 text-[12.5px] text-[var(--color-text-muted)]">
            {t.fields.length} {t.fields.length === 1 ? 'поле' : 'полів'}
          </div>
        </div>
        <div class="text-[var(--color-text-muted-2)]">›</div>
      </button>
    {/each}

    <button
      type="button"
      onclick={() => (createTableOpen = true)}
      class="flex w-full items-center gap-4 border-t border-[var(--color-border-3)] px-5 py-4 text-left text-[var(--color-pu-3)] transition hover:bg-[var(--color-pu-light)]"
    >
      <div class="grid h-11 w-11 shrink-0 place-items-center rounded-full border-2 border-dashed border-[var(--color-border-2)] text-lg text-[var(--color-text-muted)]">
        +
      </div>
      <div class="text-sm font-bold uppercase tracking-wider">Створити таблицю</div>
    </button>
  </div>

  {#if createTableOpen}
    <div class="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4" onclick={() => (createTableOpen = false)}>
      <form
        onsubmit={createTable}
        onclick={(e) => e.stopPropagation()}
        class="w-full max-w-md rounded-xl border border-[var(--color-border-3)] bg-white p-5 shadow-xl"
      >
        <h3 class="mb-4 text-base font-bold">Нова таблиця</h3>

        <label class="mb-3 block">
          <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Назва</div>
          <input
            bind:value={newTableName}
            oninput={() => { if (!newTableSlug) newTableSlug = slugify(newTableName); }}
            required
            placeholder="Угоди, Компанії, Завдання…"
            class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-sm"
          />
        </label>

        <label class="mb-3 block">
          <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Slug (URL)</div>
          <input
            bind:value={newTableSlug}
            placeholder="deals, companies…"
            pattern="^[a-z][a-z0-9_-]{'{'}1,63{'}'}$"
            class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-xs font-mono"
          />
          <div class="mt-1 text-[10.5px] text-[var(--color-text-muted-2)]">a-z, 0-9, _, -. Перший — літера</div>
        </label>

        <div class="mb-3">
          <div class="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Колір</div>
          <div class="flex gap-2">
            {#each PALETTE as p}
              <button
                type="button"
                onclick={() => (newTableColor = p.color)}
                title={p.label}
                class="h-7 w-7 rounded-full border-2"
                style="background: {p.color}; border-color: {newTableColor === p.color ? '#1f2937' : 'transparent'}"
                aria-label={p.label}
              ></button>
            {/each}
          </div>
        </div>

        <div class="mb-4">
          <div class="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Іконка</div>
          <div class="flex flex-wrap gap-1.5">
            {#each ICONS as ic}
              <button
                type="button"
                onclick={() => (newTableIcon = ic)}
                class="grid h-8 w-8 place-items-center rounded-md border transition hover:scale-105"
                style="color: {newTableColor}; {newTableIcon === ic ? `background:${newTableColor}1f; border-color:${newTableColor};` : 'border-color:var(--color-border-3);'}"
              >
                <Icon name={ic} size={17} />
              </button>
            {/each}
          </div>
        </div>

        <div class="flex justify-end gap-2">
          <button
            type="button"
            onclick={() => (createTableOpen = false)}
            class="rounded-md border border-[var(--color-border-3)] bg-white px-3 py-1.5 text-xs font-semibold"
          >
            Скасувати
          </button>
          <button type="submit" disabled={busy} class="rounded-md bg-[var(--color-pu)] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
            {busy ? 'Створення…' : 'Створити'}
          </button>
        </div>
      </form>
    </div>
  {/if}

{:else}
  <!-- ============== DETAIL / FORM VIEWS ============== -->
  {@const t = selectedTable()}
  {#if !t}
    <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
      Таблицю не знайдено. <button class="underline" onclick={backToList}>Повернутися</button>
    </div>
  {:else}
    <!-- Breadcrumb -->
    <div class="mb-3 flex items-center gap-2 text-[12px] text-[var(--color-text-muted)]">
      <button onclick={backToList} class="hover:text-[var(--color-pu-3)]">Управління таблицями</button>
      <span>›</span>
      <span class="inline-flex items-center gap-1.5 font-semibold text-[var(--color-text)]">
        <span
          class="grid h-5 w-5 place-items-center rounded text-white"
          style="background: {t.color}"
        ><Icon name={tableIconName(t.icon)} size={12} /></span>
        {t.name}
      </span>
    </div>

    {#if view.kind === 'detail'}
      <header class="mb-5 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 class="text-xl font-semibold leading-tight">{t.name}</h1>
          {#if t.description}
            <p class="mt-1 text-[12px] text-[var(--color-text-muted)]">{t.description}</p>
          {/if}
        </div>
        {#if !t.builtIn}
          <button
            onclick={() => onDeleteTable(t.slug)}
            disabled={busy}
            class="rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            Видалити таблицю
          </button>
        {/if}
      </header>

      <!-- Tabs -->
      <div class="mb-5 border-b border-[var(--color-border-3)]">
        <div class="flex gap-1">
          <button
            onclick={() => setTab('folder')}
            class="dt-tab"
            class:active={view.tab === 'folder'}
          >ПАПКА</button>
          <button
            onclick={() => setTab('fields')}
            class="dt-tab"
            class:active={view.tab === 'fields'}
          >ПОЛЯ</button>
          <button
            onclick={() => setTab('snapshots')}
            class="dt-tab"
            class:active={view.tab === 'snapshots'}
          >ЗНІМКИ</button>
        </div>
      </div>

      {#if view.tab === 'folder'}
        <div class="rounded-xl border border-[var(--color-border-3)] bg-white p-5 max-w-xl">
          <div class="mb-4 flex items-center gap-2.5">
            <span
              class="grid h-9 w-9 place-items-center rounded-full text-white"
              style="background: {fColor}"
            ><Icon name={tableIconName(fIcon)} size={18} /></span>
            <div>
              <div class="text-sm font-semibold">{fName || t.name}</div>
              <div class="text-[11px] text-[var(--color-text-muted-2)] font-mono">{t.slug}</div>
            </div>
          </div>

          <div class="grid gap-4">
            <label class="block">
              <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Slug (URL)</div>
              <input
                value={t.slug}
                disabled
                class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-xs font-mono bg-[var(--color-bg-3)] text-[var(--color-text-muted)]"
              />
              <div class="mt-1 text-[10.5px] text-[var(--color-text-muted-2)]">Slug незмінний — використовується в URL та API.</div>
            </label>

            <label class="block">
              <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Назва</div>
              <input
                bind:value={fName}
                placeholder="Назва таблиці"
                class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--color-pu)]"
              />
              {#if t.builtIn}
                <div class="mt-1 text-[10.5px] text-[var(--color-text-muted-2)]">Built-in таблиця — назву/колір/іконку можна змінювати, slug і структуру — ні.</div>
              {/if}
            </label>

            <label class="block">
              <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Опис</div>
              <textarea
                bind:value={fDesc}
                rows="2"
                placeholder="Короткий опис призначення таблиці (опційно)"
                class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--color-pu)] resize-none"
              ></textarea>
            </label>

            <div>
              <div class="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Колір</div>
              <div class="flex flex-wrap gap-2">
                {#each PALETTE as p}
                  <button
                    type="button"
                    onclick={() => (fColor = p.color)}
                    class="h-7 w-7 rounded-full border-2 transition-transform hover:scale-110"
                    style="background: {p.color}; border-color: {fColor === p.color ? 'var(--color-text)' : 'transparent'}"
                    aria-label={p.label}
                    title={p.label}
                  ></button>
                {/each}
              </div>
            </div>

            <div>
              <div class="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Іконка</div>
              <div class="flex flex-wrap gap-1.5">
                {#each ICONS as ic}
                  <button
                    type="button"
                    onclick={() => (fIcon = ic)}
                    class="grid h-8 w-8 place-items-center rounded-md border transition hover:scale-105"
                    style="color: {fColor}; {fIcon === ic ? `background:${fColor}1f; border-color:${fColor};` : 'border-color:var(--color-border-3);'}"
                  >
                    <Icon name={ic} size={17} />
                  </button>
                {/each}
              </div>
            </div>

            <div class="flex items-center gap-3 pt-1">
              <button
                type="button"
                onclick={saveFolder}
                disabled={!folderDirty || folderBusy || !fName.trim()}
                class="btn-pu rounded-lg px-4 py-2 text-xs font-semibold disabled:opacity-50"
              >{folderBusy ? 'Збереження…' : 'Зберегти зміни'}</button>
              {#if folderSaved}
                <span class="inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-600"><Icon name="statusDone" size={14} /> Збережено</span>
              {/if}
            </div>
          </div>
        </div>
      {/if}

      {#if view.tab === 'fields'}
        <div class="mb-3 flex items-center gap-2">
          <span
            class="grid h-5 w-5 place-items-center rounded text-white"
            style="background: {t.color}"
          ><Icon name={tableIconName(t.icon)} size={12} /></span>
          <span class="text-sm font-semibold">{t.name}</span>
          <span class="text-base font-semibold">· Налаштувати поля</span>
        </div>

        <FieldsBoard
          table={t}
          onReload={load}
          onOpenField={openFieldEdit}
          onAddField={openPickType}
        />
      {/if}

      {#if view.tab === 'snapshots'}
        <SnapshotsPanel slug={view.slug} onChanged={load} />
      {/if}
    {/if}

    <!-- ============== PICK TYPE ============== -->
    {#if view.kind === 'pick-type'}
      {@const groups = groupedFieldTypes()}
      <div class="rounded-xl border border-[var(--color-border-3)] bg-white">
        <div class="flex items-center gap-3 border-b border-[var(--color-border-3)] px-5 py-4">
          <button onclick={backFromForm} class="text-lg text-[var(--color-text-muted)] hover:text-[var(--color-text)]">←</button>
          <h2 class="text-base font-bold">Додати нове поле</h2>
        </div>

        <div class="px-5 py-4">
          <div class="mb-2 text-[11px] font-semibold text-[var(--color-text-muted)]">Типи полів:</div>
          <div class="grid grid-cols-2 gap-1.5">
            {#each groups.common as ft (ft.key)}
              <button
                onclick={() => selectFieldType(ft.key)}
                class="flex items-center gap-3 rounded-lg border border-transparent px-3 py-2 text-left text-sm hover:border-[var(--color-pu-3)] hover:bg-[var(--color-pu-light)]"
              >
                <span class="grid h-7 w-7 shrink-0 place-items-center rounded text-[12px] font-bold text-[var(--color-text-muted)]">
                  <FieldTypeIcon type={ft.key} size={14} />
                </span>
                <span>{ft.label}</span>
              </button>
            {/each}
          </div>

          <div class="mt-4 mb-2 text-[11px] font-semibold text-[var(--color-text-muted)]">Розширені:</div>
          <div class="grid grid-cols-2 gap-1.5">
            {#each groups.advanced as ft (ft.key)}
              <button
                onclick={() => selectFieldType(ft.key)}
                class="flex items-center gap-3 rounded-lg border border-transparent px-3 py-2 text-left text-sm hover:border-[var(--color-pu-3)] hover:bg-[var(--color-pu-light)]"
              >
                <span class="grid h-7 w-7 shrink-0 place-items-center rounded text-[12px] font-bold text-[var(--color-text-muted)]">
                  <FieldTypeIcon type={ft.key} size={14} />
                </span>
                <span>{ft.label}</span>
              </button>
            {/each}
          </div>
        </div>
      </div>
    {/if}

    <!-- ============== FIELD FORM ============== -->
    {#if view.kind === 'field-form'}
      {@const ft = fieldType(view.type)}
      {@const existing = selectedField()}
      {@const readOnly = !!existing?.builtIn}
      <div class="rounded-xl border border-[var(--color-border-3)] bg-white">
        <div class="flex items-center gap-3 border-b border-[var(--color-border-3)] px-5 py-4">
          <button onclick={backFromForm} class="text-lg text-[var(--color-text-muted)] hover:text-[var(--color-text)]">←</button>
          <h2 class="text-base font-bold">Налаштування поля</h2>
          {#if readOnly}
            <span class="ml-auto rounded-full bg-[var(--color-pu-light)] px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-[var(--color-pu-3)]">
              Built-in · read-only
            </span>
          {/if}
        </div>

        <div class="px-5 py-4">
          <!-- Type chip + change type -->
          <div class="mb-3 flex items-start justify-between gap-3 flex-wrap">
            <div class="flex items-center gap-2 rounded-md bg-[var(--color-bg-3)] px-3 py-1.5">
              <span class="grid h-6 w-6 place-items-center rounded text-[12px] font-bold text-[var(--color-text-muted)]"><FieldTypeIcon type={ft.key} size={14} /></span>
              <span class="text-sm font-semibold">{ft.label}</span>
            </div>
            {#if !view.fieldId && !readOnly}
              <button
                onclick={changeTypeFromForm}
                class="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border-3)] bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text)] hover:bg-[var(--color-bg-3)]"
              >
                <Icon name="settings" size={13} /> Змінити тип
              </button>
            {/if}
          </div>
          <p class="mb-5 text-[12px] text-[var(--color-text-muted)]">{typeDescription(view.type)}</p>

          <!-- Field name -->
          <label class="mb-5 block">
            <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Назва поля *</div>
            <input
              bind:value={formLabel}
              disabled={readOnly}
              required
              class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-sm disabled:bg-[var(--color-bg-3)] disabled:text-[var(--color-text-muted)]"
            />
          </label>

          {#if existing}
            <div class="mb-5 rounded-md bg-[var(--color-bg-3)] px-3 py-2 text-[11px] text-[var(--color-text-muted)]">
              Identifier: <code class="font-mono">{existing.fieldKey}</code>
            </div>
          {/if}

          <!-- Група (опційно) — для built-in полів недоступна -->
          {#if !readOnly}
            <label class="mb-5 block">
              <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Група</div>
              <input
                bind:value={formGroupName}
                placeholder="Без групи (за замовч.)"
                list="dt-existing-groups"
                class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-sm"
              />
              <datalist id="dt-existing-groups">
                {#each existingGroupNames(view.slug) as g (g)}
                  <option value={g}></option>
                {/each}
              </datalist>
              <div class="mt-1 text-[10.5px] text-[var(--color-text-muted-2)]">
                Поле з'явиться у блоці з цією назвою. Пусто = "Додаткові".
              </div>
            </label>
          {/if}

          <!-- Show in list — чи показувати колонкою у Persons/Payments/CustomTable. -->
          {#if !readOnly}
            <label class="mb-5 flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                bind:checked={formShowInList}
                class="mt-0.5 h-4 w-4"
              />
              <div>
                <div class="text-[11.5px] font-semibold text-[var(--color-text)]">Показувати у списку</div>
                <div class="text-[10.5px] text-[var(--color-text-muted-2)]">
                  Якщо вимкнено — поле редагується тільки у деталях запису, але не з'являється колонкою у таблиці.
                </div>
              </div>
            </label>
          {/if}

          <!-- Options для dropdown — список варіантів. -->
          {#if view.type === 'dropdown' && !readOnly}
            <label class="mb-5 block">
              <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Варіанти (по одному в рядку)</div>
              <textarea
                bind:value={formOptionsText}
                rows="5"
                placeholder={'Варіант 1\nВаріант 2\nВаріант 3'}
                class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 font-mono text-sm"
              ></textarea>
              <div class="mt-1 text-[10.5px] text-[var(--color-text-muted-2)]">
                Користувачі зможуть обирати тільки з цього списку. Порядок зберігається.
              </div>
            </label>
          {/if}

          <!-- Per-field permissions (Phase 4). -->
          {#if !readOnly}
            <div class="mb-5 grid grid-cols-2 gap-3">
              <label class="block">
                <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">
                  Бачить роль ≥
                </div>
                <select
                  bind:value={formMinViewRole}
                  class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-3 py-2 text-sm"
                >
                  <option value="">Усі (без обмежень)</option>
                  <option value="READER">READER</option>
                  <option value="ANALYST">ANALYST</option>
                  <option value="ADMIN">ADMIN</option>
                </select>
              </label>
              <label class="block">
                <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">
                  Редагує роль ≥
                </div>
                <select
                  bind:value={formMinEditRole}
                  class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-3 py-2 text-sm"
                >
                  <option value="">За PATCH (зазвичай ADMIN)</option>
                  <option value="READER">READER</option>
                  <option value="ANALYST">ANALYST</option>
                  <option value="ADMIN">ADMIN</option>
                </select>
              </label>
            </div>
          {/if}

          <!-- Formula expression для type='formula'. -->
          {#if view.type === 'formula' && !readOnly}
            <label class="mb-5 block">
              <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Формула</div>
              <textarea
                bind:value={formFormulaExpression}
                rows="4"
                placeholder={'round({ltvCents} / 100)\nmax({paymentCount} * 5, 10)\nabs({balance} - 1000)\navg({a}, {b}, {c})'}
                class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 font-mono text-sm"
              ></textarea>
              <div class="mt-1 text-[10.5px] text-[var(--color-text-muted-2)]">
                Підтримує +, -, *, /, дужки, числа, <code>{'{'}fieldKey{'}'}</code> substitution.
                Функції: <code>round / floor / ceil / abs / max / min / sum / avg</code>.
                Без eval — парсер ізольований.
              </div>
            </label>
          {/if}

          <!-- Stat config для type='stat'. V1 MVP: target_slug фіксований 'payments'. -->
          {#if view.type === 'stat' && !readOnly}
            <div class="mb-5 rounded-md border border-[var(--color-border-3)] p-3">
              <div class="mb-2 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">
                Stat — aggregate over related
              </div>
              <div class="grid grid-cols-2 gap-3">
                <label class="block">
                  <div class="mb-1 text-[10.5px] text-[var(--color-text-muted-2)]">Агрегатор</div>
                  <select
                    bind:value={formStatAggregator}
                    class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-3 py-2 text-sm"
                  >
                    <option value="count">count — кількість</option>
                    <option value="sum">sum — сума</option>
                    <option value="avg">avg — середнє</option>
                    <option value="min">min</option>
                    <option value="max">max</option>
                  </select>
                </label>
                <label class="block">
                  <div class="mb-1 text-[10.5px] text-[var(--color-text-muted-2)]">Таблиця-джерело</div>
                  <select
                    bind:value={formStatTargetSlug}
                    class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-3 py-2 text-sm"
                  >
                    <option value="payments">💳 Платежі</option>
                  </select>
                </label>
                <label class="block">
                  <div class="mb-1 text-[10.5px] text-[var(--color-text-muted-2)]">Link field</div>
                  <select
                    bind:value={formStatLinkField}
                    class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-3 py-2 text-sm"
                  >
                    <option value="personId">personId (→ Person)</option>
                    <option value="productId">productId (→ Product)</option>
                  </select>
                </label>
                {#if formStatAggregator !== 'count'}
                  <label class="block">
                    <div class="mb-1 text-[10.5px] text-[var(--color-text-muted-2)]">Target field</div>
                    <select
                      bind:value={formStatTargetField}
                      class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-3 py-2 text-sm"
                    >
                      <option value="amountCents">amountCents — сума у копійках</option>
                      <option value="feeCents">feeCents — комісія</option>
                      <option value="netCents">netCents — нетто</option>
                      <option value="refundAmountCents">refundAmountCents — сума повернення</option>
                      <option value="authAmountCents">authAmountCents — сума авторизації</option>
                      <option value="installmentParts">installmentParts — кількість частин</option>
                    </select>
                  </label>
                {/if}
              </div>
              <div class="mt-2 text-[10.5px] text-[var(--color-text-muted-2)]">
                Обчислюється бекендом на read (batch для list-view). Поле readonly у формах.
              </div>
            </div>
          {/if}

          <!-- Target table для linked_record / linked_summary. -->
          {#if (view.type === 'linked_record' || view.type === 'linked_summary') && !readOnly}
            {@const currentSlug = view.slug}
            <label class="mb-4 block">
              <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Цільова таблиця</div>
              <select
                bind:value={formTargetSlug}
                class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-3 py-2 text-sm"
              >
                <option value="">— оберіть таблицю —</option>
                {#each tables.filter((t) => t.slug !== currentSlug) as t (t.slug)}
                  <option value={t.slug}>{t.icon} {t.name}</option>
                {/each}
              </select>
            </label>

            <!-- Cardinality: one vs many -->
            {#if view.type === 'linked_record'}
              <div class="mb-5">
                <div class="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Кількість посилань</div>
                <div class="flex gap-2">
                  <button
                    type="button"
                    onclick={() => (formCardinality = 'one')}
                    class="flex-1 rounded-lg border py-2 text-xs font-semibold transition-colors
                      {formCardinality === 'one'
                        ? 'bg-[var(--color-pu)] text-white border-[var(--color-pu)]'
                        : 'bg-white border-[var(--color-border-3)] text-[var(--color-text-muted)] hover:border-[var(--color-pu-2)]'}"
                  ><span class="inline-flex items-center justify-center gap-1.5"><Icon name="url" size={13} /> Один запис</span></button>
                  <button
                    type="button"
                    onclick={() => (formCardinality = 'many')}
                    class="flex-1 rounded-lg border py-2 text-xs font-semibold transition-colors
                      {formCardinality === 'many'
                        ? 'bg-[var(--color-pu)] text-white border-[var(--color-pu)]'
                        : 'bg-white border-[var(--color-border-3)] text-[var(--color-text-muted)] hover:border-[var(--color-pu-2)]'}"
                  ><span class="inline-flex items-center justify-center gap-1"><Icon name="url" size={13} /><Icon name="url" size={13} /> Кілька записів</span></button>
                </div>
                <div class="mt-1 text-[10.5px] text-[var(--color-text-muted-2)]">
                  {formCardinality === 'many'
                    ? 'Зберігає масив UUID — дозволяє вибрати кілька записів з цільової таблиці.'
                    : 'Зберігає один UUID — стандартне посилання на один запис.'}
                </div>
              </div>
            {/if}
          {/if}

        </div>

        <!-- Footer -->
        <div class="flex justify-end gap-2 border-t border-[var(--color-border-3)] px-5 py-3">
          <button
            onclick={backFromForm}
            class="rounded-md border border-[var(--color-border-3)] bg-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)]"
          >
            {readOnly ? 'Закрити' : 'Скасувати'}
          </button>
          {#if !readOnly}
            <button
              onclick={saveField}
              disabled={busy}
              class="rounded-md bg-[var(--color-pu)] px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white disabled:opacity-50"
            >
              {busy ? 'Збереження…' : 'Готово'}
            </button>
          {/if}
        </div>
      </div>
    {/if}
  {/if}
{/if}

<style>
  :global(.dt-tab) {
    padding: 9px 18px;
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 1px;
    color: var(--color-text-muted);
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    background: none;
    cursor: pointer;
  }
  :global(.dt-tab:hover) {
    color: var(--color-text);
  }
  :global(.dt-tab.active) {
    color: var(--color-pu-3);
    border-bottom-color: var(--color-pu-3);
  }
</style>
